import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  message,
  Space,
  Tag,
  Card,
  Descriptions,
  Empty,
  DatePicker,
  Select,
  Input,
  Pagination,
  Row,
  Col,
} from 'antd';
import {
  ReloadOutlined,
  EyeOutlined,
  FilterOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../config/apiClient';
import MainLayout from '../layout/layout';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

const DescuentosCreditos = () => {
  const [descuentos, setDescuentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalDetalleVisible, setModalDetalleVisible] = useState(false);
  const [descuentoSeleccionado, setDescuentoSeleccionado] = useState(null);
  const [filtros, setFiltros] = useState({
    fechaDesde: null,
    fechaHasta: null,
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Cargar descuentos
  const loadDescuentos = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('pageSize', pageSize.toString());
      
      if (filtros.fechaDesde) {
        params.append('fechaDesde', filtros.fechaDesde.format('YYYY-MM-DD'));
      }
      if (filtros.fechaHasta) {
        params.append('fechaHasta', filtros.fechaHasta.format('YYYY-MM-DD'));
      }
      
      const queryString = params.toString();
      const url = `/descuento-creditos?${queryString}`;
      
      const response = await apiClient.get(url);
      if (response.ok) {
        const result = await response.json();
        setDescuentos(result.data || []);
        setPagination({
          current: result.page || 1,
          pageSize: result.pageSize || 10,
          total: result.total || 0,
        });
      } else {
        message.error('Error al cargar los descuentos de créditos');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar los descuentos de créditos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDescuentos(pagination.current, pagination.pageSize);
  }, []);

  // Manejar cambio de página
  const handleTableChange = (page, pageSize) => {
    setPagination({ ...pagination, current: page, pageSize });
    loadDescuentos(page, pageSize);
  };

  // Ver detalle de un descuento
  const verDetalle = async (id) => {
    try {
      const response = await apiClient.get(`/descuento-creditos/${id}`);
      if (response.ok) {
        const descuento = await response.json();
        setDescuentoSeleccionado(descuento);
        setModalDetalleVisible(true);
      } else {
        message.error('Error al cargar el detalle del descuento');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar el detalle del descuento');
    }
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    setPagination({ ...pagination, current: 1 });
    loadDescuentos(1, pagination.pageSize);
  };

  // Limpiar filtros
  const limpiarFiltros = () => {
    setFiltros({
      fechaDesde: null,
      fechaHasta: null,
    });
    setPagination({ ...pagination, current: 1 });
    setTimeout(() => {
      loadDescuentos(1, pagination.pageSize);
    }, 100);
  };

  // Manejar cambio de rango de fechas
  const handleFechaChange = (dates) => {
    if (dates && dates.length === 2) {
      setFiltros({
        fechaDesde: dates[0],
        fechaHasta: dates[1],
      });
    } else {
      setFiltros({
        fechaDesde: null,
        fechaHasta: null,
      });
    }
  };

  // Obtener nombre del día de la semana
  const getNombreDiaSemana = (dia) => {
    const dias = ['', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
    return dias[dia] || '';
  };

  // Columnas de la tabla
  const columns = [
    {
      title: 'Fecha Turno',
      dataIndex: 'des_fecha_turno',
      key: 'des_fecha_turno',
      render: (fecha) => fecha ? dayjs(fecha).format('DD/MM/YYYY') : '-',
      sorter: true,
    },
    {
      title: 'Hora Descuento',
      dataIndex: 'des_hora_descuento',
      key: 'des_hora_descuento',
      render: (hora) => hora ? dayjs(hora).format('DD/MM/YYYY HH:mm:ss') : '-',
      sorter: true,
    },
    {
      title: 'Alumno',
      key: 'alumno',
      render: (_, record) => `${record.alu_nombre || ''} ${record.alu_apellido || ''}`.trim(),
    },
    {
      title: 'Turno',
      key: 'turno',
      render: (_, record) => {
        if (record.tur_tipo === 'FIJO') {
          return `${getNombreDiaSemana(record.tur_dia_semana)} ${record.tur_hora_desde || ''}`;
        } else {
          return `${dayjs(record.tur_fecha_especifica).format('DD/MM/YYYY')} ${record.tur_hora_desde || ''}`;
        }
      },
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => verDetalle(record.des_id)}
          >
            Ver Detalle
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <div style={{ padding: '20px' }}>
        <Card>
          <div style={{ marginBottom: '20px' }}>
            <h2 style={{ margin: 0, marginBottom: '20px' }}>
              Descuentos de Créditos Automáticos
            </h2>
            
            {/* Filtros */}
            <Row gutter={16} style={{ marginBottom: '20px' }}>
              <Col xs={24} sm={12} md={8}>
                <RangePicker
                  style={{ width: '100%' }}
                  placeholder={['Fecha desde', 'Fecha hasta']}
                  value={filtros.fechaDesde && filtros.fechaHasta ? [filtros.fechaDesde, filtros.fechaHasta] : null}
                  onChange={handleFechaChange}
                  format="DD/MM/YYYY"
                />
              </Col>
              <Col xs={24} sm={12} md={16}>
                <Space>
                  <Button
                    type="primary"
                    icon={<FilterOutlined />}
                    onClick={aplicarFiltros}
                  >
                    Aplicar Filtros
                  </Button>
                  <Button
                    icon={<ClearOutlined />}
                    onClick={limpiarFiltros}
                  >
                    Limpiar
                  </Button>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() => loadDescuentos(pagination.current, pagination.pageSize)}
                  >
                    Actualizar
                  </Button>
                </Space>
              </Col>
            </Row>

            {/* Tabla */}
            <Table
              columns={columns}
              dataSource={descuentos}
              loading={loading}
              rowKey="des_id"
              pagination={false}
              scroll={{ x: 'max-content' }}
            />

            {/* Paginación */}
            <div style={{ marginTop: '20px', textAlign: 'right' }}>
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                showSizeChanger
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} de ${total} descuentos`
                }
                onChange={handleTableChange}
                onShowSizeChange={handleTableChange}
                pageSizeOptions={['10', '20', '50', '100']}
              />
            </div>
          </div>
        </Card>

        {/* Modal de Detalle */}
        <Modal
          title="Detalle del Descuento de Crédito"
          open={modalDetalleVisible}
          onCancel={() => {
            setModalDetalleVisible(false);
            setDescuentoSeleccionado(null);
          }}
          footer={[
            <Button key="close" onClick={() => {
              setModalDetalleVisible(false);
              setDescuentoSeleccionado(null);
            }}>
              Cerrar
            </Button>,
          ]}
          width={800}
        >
          {descuentoSeleccionado ? (
            <Descriptions bordered column={1}>
              <Descriptions.Item label="ID Descuento">
                {descuentoSeleccionado.des_id}
              </Descriptions.Item>
              <Descriptions.Item label="Hora del Descuento">
                {dayjs(descuentoSeleccionado.des_hora_descuento).format('DD/MM/YYYY HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label="Fecha del Turno">
                {dayjs(descuentoSeleccionado.des_fecha_turno).format('DD/MM/YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label="Alumno">
                {`${descuentoSeleccionado.alu_nombre || ''} ${descuentoSeleccionado.alu_apellido || ''}`.trim()}
              </Descriptions.Item>
              <Descriptions.Item label="Email Alumno">
                {descuentoSeleccionado.alu_email || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Turno ID">
                {descuentoSeleccionado.des_turno}
              </Descriptions.Item>
              <Descriptions.Item label="Tipo de Turno">
                <Tag color={descuentoSeleccionado.tur_tipo === 'FIJO' ? 'blue' : 'green'}>
                  {descuentoSeleccionado.tur_tipo === 'FIJO' ? 'Fijo' : 'Evento'}
                </Tag>
              </Descriptions.Item>
              {descuentoSeleccionado.tur_tipo === 'FIJO' ? (
                <Descriptions.Item label="Día de la Semana">
                  {getNombreDiaSemana(descuentoSeleccionado.tur_dia_semana)}
                </Descriptions.Item>
              ) : (
                <Descriptions.Item label="Fecha Específica">
                  {dayjs(descuentoSeleccionado.tur_fecha_especifica).format('DD/MM/YYYY')}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Hora">
                {descuentoSeleccionado.tur_hora_desde} - {descuentoSeleccionado.tur_hora_hasta}
              </Descriptions.Item>
              <Descriptions.Item label="Cancha">
                {descuentoSeleccionado.cancha_descripcion || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Categoría">
                {descuentoSeleccionado.categoria_descripcion || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Crédito ID">
                {descuentoSeleccionado.des_credito}
              </Descriptions.Item>
              <Descriptions.Item label="Plan">
                {descuentoSeleccionado.plan_nombre || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Fecha de Vencimiento del Crédito">
                {descuentoSeleccionado.cre_fecha_vencimiento
                  ? dayjs(descuentoSeleccionado.cre_fecha_vencimiento).format('DD/MM/YYYY')
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Fecha de Utilización del Crédito">
                {descuentoSeleccionado.cre_fecha_utilizacion
                  ? dayjs(descuentoSeleccionado.cre_fecha_utilizacion).format('DD/MM/YYYY HH:mm:ss')
                  : '-'}
              </Descriptions.Item>
            </Descriptions>
          ) : (
            <Empty description="No hay información disponible" />
          )}
        </Modal>
      </div>
    </MainLayout>
  );
};

export default DescuentosCreditos;

